import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { assert } from '@ember/debug';
import { action } from '@ember/object';
export interface InputControlSignature {
    Element: HTMLInputElement;
    Args: {
        isConfirmed?: boolean;
        hasError?: boolean;
        onChange?: (value: string, e: Event | InputEvent) => void;
        value?: string;
    };
}
const STYLES = {
    base: [
        'form-input',
        'text-16',
        'sm:text-14',
        'px-0',
        'py-2',
        'sm:py-0',
        'w-full',
        'bg-transparent',
        'placeholder:text-grey',
        'border-b-grey',
        'border-l-0',
        'border-r-0',
        'border-t-0',
        'focus:border-b-black',
        'focus:ring-0',
        '[&:disabled]:border-b-grey-medium',
        '[&:disabled]:text-grey-medium',
        '[&:disabled]:placeholder:text-transparent',
        // right aligned inputs
        '[appearance:textfield]',
        '[&[type="number"]::-webkit-inner-spin-button]:appearance-none',
        '[&[type="number"]::-webkit-outer-spin-button]:appearance-none',
        '[&[type="number"]]:appearance-textfield',
        '[&[type="number"]]:text-right',
        '[&[type="date"]::-webkit-datetime-edit]:justify-end',
        '[&[type="datetime-local"]::-webkit-datetime-edit]:justify-end' // For Chrome and Safari
    ],
    confirmed: [
        'text-teal',
        'border-b-teal',
        'focus:border-b-teal',
        'focus:shadow-confirmed-input'
    ],
    error: [
        'text-red',
        'border-b-red',
        'focus:border-b-red',
        'focus:shadow-error-input'
    ]
} as const;
export default class WrFormControlsInputComponent extends Component<InputControlSignature> {
    get styles() {
        let styles1;
        if (this.args.isConfirmed) styles1 = [
            ...STYLES.base,
            ...STYLES.confirmed
        ];
        else if (this.args.hasError) styles1 = [
            ...STYLES.base,
            ...STYLES.error
        ];
        else styles1 = [
            ...STYLES.base,
            'focus:shadow-input'
        ];
        return styles1.join(' ');
    }
    @action
    handleInput(e1: Event | InputEvent): void {
        assert('Expected HTMLInputElement', e1.target instanceof HTMLInputElement);
        this.args.onChange?.(e1.target.value, e1);
    }
    static{
        template(`
    <input
      class={{this.styles}}
      value={{@value}}
      {{on 'input' this.handleInput}}
      ...attributes
    />
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJzb3VyY2VzIjpbIi9ob21lL3NlbWVpYS9wYWNrYWdlcy9lbWJlci1hZGRvbnMvdjIvZW1iZXItd2lzZXIvc3JjL2NvbXBvbmVudHMvd3IvZm9ybS9jb250cm9scy9pbnB1dC5ndHMiXSwic291cmNlc0NvbnRlbnQiOlsiaW1wb3J0IENvbXBvbmVudCBmcm9tICdAZ2xpbW1lci9jb21wb25lbnQnO1xuaW1wb3J0IHsgb24gfSBmcm9tICdAZW1iZXIvbW9kaWZpZXInO1xuaW1wb3J0IHsgYXNzZXJ0IH0gZnJvbSAnQGVtYmVyL2RlYnVnJztcbmltcG9ydCB7IGFjdGlvbiB9IGZyb20gJ0BlbWJlci9vYmplY3QnO1xuXG5leHBvcnQgaW50ZXJmYWNlIElucHV0Q29udHJvbFNpZ25hdHVyZSB7XG4gIEVsZW1lbnQ6IEhUTUxJbnB1dEVsZW1lbnQ7XG4gIEFyZ3M6IHtcbiAgICBpc0NvbmZpcm1lZD86IGJvb2xlYW47XG4gICAgaGFzRXJyb3I_OiBib29sZWFuO1xuICAgIG9uQ2hhbmdlPzogKHZhbHVlOiBzdHJpbmcsIGU6IEV2ZW50IHwgSW5wdXRFdmVudCkgPT4gdm9pZDtcbiAgICB2YWx1ZT86IHN0cmluZztcbiAgfTtcbn1cblxuY29uc3QgU1RZTEVTID0ge1xuICBiYXNlOiBbXG4gICAgJ2Zvcm0taW5wdXQnLFxuICAgICd0ZXh0LTE2JyxcbiAgICAnc206dGV4dC0xNCcsXG4gICAgJ3B4LTAnLFxuICAgICdweS0yJyxcbiAgICAnc206cHktMCcsXG4gICAgJ3ctZnVsbCcsXG4gICAgJ2JnLXRyYW5zcGFyZW50JyxcbiAgICAncGxhY2Vob2xkZXI6dGV4dC1ncmV5JyxcbiAgICAnYm9yZGVyLWItZ3JleScsXG4gICAgJ2JvcmRlci1sLTAnLFxuICAgICdib3JkZXItci0wJyxcbiAgICAnYm9yZGVyLXQtMCcsXG4gICAgJ2ZvY3VzOmJvcmRlci1iLWJsYWNrJyxcbiAgICAnZm9jdXM6cmluZy0wJyxcbiAgICAnWyY6ZGlzYWJsZWRdOmJvcmRlci1iLWdyZXktbWVkaXVtJyxcbiAgICAnWyY6ZGlzYWJsZWRdOnRleHQtZ3JleS1tZWRpdW0nLFxuICAgICdbJjpkaXNhYmxlZF06cGxhY2Vob2xkZXI6dGV4dC10cmFuc3BhcmVudCcsXG5cbiAgICAvLyByaWdodCBhbGlnbmVkIGlucHV0c1xuICAgICdbYXBwZWFyYW5jZTp0ZXh0ZmllbGRdJywgLy8gRm9yIEZpcmVmb3hcbiAgICAnWyZbdHlwZT1cIm51bWJlclwiXTo6LXdlYmtpdC1pbm5lci1zcGluLWJ1dHRvbl06YXBwZWFyYW5jZS1ub25lJywgLy8gRm9yIENocm9tZSBhbmQgU2FmYXJpXG4gICAgJ1smW3R5cGU9XCJudW1iZXJcIl06Oi13ZWJraXQtb3V0ZXItc3Bpbi1idXR0b25dOmFwcGVhcmFuY2Utbm9uZScsIC8vIEZvciBDaHJvbWUgYW5kIFNhZmFyaVxuICAgICdbJlt0eXBlPVwibnVtYmVyXCJdXTphcHBlYXJhbmNlLXRleHRmaWVsZCcsXG4gICAgJ1smW3R5cGU9XCJudW1iZXJcIl1dOnRleHQtcmlnaHQnLFxuICAgICdbJlt0eXBlPVwiZGF0ZVwiXTo6LXdlYmtpdC1kYXRldGltZS1lZGl0XTpqdXN0aWZ5LWVuZCcsIC8vIEZvciBDaHJvbWUgYW5kIFNhZmFyaVxuICAgICdbJlt0eXBlPVwiZGF0ZXRpbWUtbG9jYWxcIl06Oi13ZWJraXQtZGF0ZXRpbWUtZWRpdF06anVzdGlmeS1lbmQnIC8vIEZvciBDaHJvbWUgYW5kIFNhZmFyaVxuICBdLFxuICBjb25maXJtZWQ6IFtcbiAgICAndGV4dC10ZWFsJyxcbiAgICAnYm9yZGVyLWItdGVhbCcsXG4gICAgJ2ZvY3VzOmJvcmRlci1iLXRlYWwnLFxuICAgICdmb2N1czpzaGFkb3ctY29uZmlybWVkLWlucHV0J1xuICBdLFxuICBlcnJvcjogW1xuICAgICd0ZXh0LXJlZCcsXG4gICAgJ2JvcmRlci1iLXJlZCcsXG4gICAgJ2ZvY3VzOmJvcmRlci1iLXJlZCcsXG4gICAgJ2ZvY3VzOnNoYWRvdy1lcnJvci1pbnB1dCdcbiAgXVxufSBhcyBjb25zdDtcblxuZXhwb3J0IGRlZmF1bHQgY2xhc3MgV3JGb3JtQ29udHJvbHNJbnB1dENvbXBvbmVudCBleHRlbmRzIENvbXBvbmVudDxJbnB1dENvbnRyb2xTaWduYXR1cmU-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